import React, { useEffect, useState, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import classNames from 'classnames';
import { icons } from './icons';
import variants, { SUCCESS } from './variants';

function onActionClick(id, onClick, onCloseClick) {
    const result = onClick(id);
    if (typeof result?.then === 'function') {
        result.then(() => onCloseClick());
    } else {
        onCloseClick();
    }
}

export default function Notification({
    id,
    message,
    title,
    removeNotification,
    variant = SUCCESS,
    className,
    autoDelete = true,
    hideClose = false,
    actions = [],
}) {
    const [shownow, setShow] = useState(false);
    const afterLeave = useCallback(
        () => removeNotification(id),
        [removeNotification, id],
    );
    const onCloseClick = useCallback(() => setShow(false), [setShow]);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [hasBeenHovered, setHasBeenHovered] = useState(false);

    useEffect(() => {
        const expeditedTimeout = 1000;
        const timeoutDuration = hasBeenHovered ? expeditedTimeout : autoDelete;
        const showTimeout = setTimeout(() => setShow(true), 100);
        let hideTimeout;
        if (timeoutDuration && !isHovered) {
            hideTimeout = setTimeout(() => setShow(false), timeoutDuration);
        }

        return () => {
            clearTimeout(showTimeout);
            if (hideTimeout) clearTimeout(hideTimeout);
        };
    }, [setShow, autoDelete, isHovered, hasBeenHovered]);

    const hasActions = actions.length > 0;

    const variantObject = variants[variant];

    return (
        <Transition
            show={shownow}
            enter="transform ease-out duration-500 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-full"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-300"
            leaveFrom="opacity-100 sm:translate-y-0"
            leaveTo="opacity-0 sm:translate-y-full"
            afterLeave={afterLeave}
        >
            <div
                className={classNames(
                    'flex relative w-108 shadow-lg pointer-events-auto rounded-md border',
                    variantObject.border,
                    variantObject.textColor,
                    {
                        'divide-x': hasActions,
                        [variantObject.divider]: hasActions,
                    },
                )}
                onMouseEnter={() => {
                    setIsHovered(true);
                    setHasBeenHovered(true);
                }}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div
                    className={classNames(
                        'w-full flex flex-col place-content-center rounded-tl-md rounded-bl-md',
                        variantObject.textColor,
                        {
                            'rounded-tr-md rounded-br-md': !hasActions,
                        },
                    )}
                >
                    <div className="relative p-6">
                        <div className="flex justify-between mb-3">
                            <div className="flex items-center">
                                <div>{icons[variant]}</div>
                                <div
                                    className={classNames(
                                        'font-medium paragraph',
                                        variantObject.textColor,
                                    )}
                                >
                                    {title}
                                </div>
                            </div>
                            {!hasActions && !hideClose && (
                                <div>
                                    <Close
                                        variantClasses={variantObject.close}
                                        onClick={onCloseClick}
                                    />
                                </div>
                            )}
                        </div>
                        <div
                            className={classNames(
                                'heading-5 text-left',
                                variantObject.textColor,
                            )}
                        >
                            {message}
                        </div>
                    </div>
                </div>
                {hasActions && (
                    <div
                        className={classNames(
                            'w-1/4 flex flex-col divide-y',
                            variantObject.divider,
                        )}
                    >
                        {actions.map((action, index) => {
                            return (
                                <button
                                    key={`${id}-action-${index}`}
                                    onClick={() =>
                                        onActionClick(
                                            id,
                                            action.onClick,
                                            onCloseClick,
                                        )
                                    }
                                    className={classNames(
                                        'flex flex-col font-semibold focus:outline-none focus-visible:ring-2 focus-visible:z-10 place-items-center h-full justify-center cursor-pointer border-transparent',
                                        variantObject.actionButtons,
                                        {
                                            'rounded-tr-md': index === 0,
                                            'rounded-br-md':
                                                index === actions.length - 1,
                                        },
                                    )}
                                >
                                    {action.text}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </Transition>
    );
}

function Close({ onClick, variantClasses }) {
    return (
        <button
            onClick={onClick}
            className={classNames(
                'w-3.5 h-3.5 focus:outline-none focus-visible:ring-2',
                variantClasses,
            )}
        >
            <svg
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M8.16102 7L13.2947 0.888672C13.3808 0.78711 13.3084 0.632812 13.1754 0.632812H11.6148C11.5229 0.632812 11.4349 0.673828 11.3742 0.744141L7.14014 5.78516L2.90605 0.744141C2.84738 0.673828 2.75938 0.632812 2.6655 0.632812H1.10485C0.971867 0.632812 0.899506 0.78711 0.985557 0.888672L6.11927 7L0.985557 13.1113C0.966281 13.134 0.953915 13.1617 0.949926 13.1911C0.945937 13.2206 0.950493 13.2505 0.963054 13.2775C0.975614 13.3044 0.995652 13.3272 1.02079 13.3431C1.04592 13.359 1.0751 13.3674 1.10485 13.3672H2.6655C2.75742 13.3672 2.84543 13.3262 2.90605 13.2559L7.14014 8.21484L11.3742 13.2559C11.4329 13.3262 11.5209 13.3672 11.6148 13.3672H13.1754C13.3084 13.3672 13.3808 13.2129 13.2947 13.1113L8.16102 7Z"
                    className="fill-current"
                />
            </svg>
        </button>
    );
}
